import { useMemo } from 'react';
import { usePartnerSettings, type DocumentType } from '@/hooks/usePartnerSettings';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { differenceInDays } from 'date-fns';

export type DocumentStatus = 'verified' | 'pending' | 'rejected' | 'expired' | 'expiring_soon' | 'missing';

export interface RequiredDocumentStatus {
  documentType: DocumentType;
  status: DocumentStatus;
  uploadedDocument?: any;
  expiryWarning?: string;
  rejectionReason?: string;
  daysUntilExpiry?: number;
}

export function useRequiredDocuments(companyId: string | null) {
  const { settings, isLoading: settingsLoading } = usePartnerSettings();

  const { data: documents, isLoading: documentsLoading } = useQuery({
    queryKey: ['partner-documents', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('partner_documents')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });

  const requiredDocuments = useMemo(() => {
    if (!settings?.document_types || !documents) return [];

    const requiredTypes = settings.document_types.filter(
      (dt: DocumentType) => dt.required_for_prequalification
    );

    return requiredTypes.map((docType: DocumentType): RequiredDocumentStatus => {
      // Find all documents of this type (match by ID or name for backward compatibility)
      const matchingDocs = documents.filter(
        (doc) => doc.document_type === docType.name || doc.document_type === docType.id
      );

      if (matchingDocs.length === 0) {
        return {
          documentType: docType,
          status: 'missing',
        };
      }

      // Find the best document (prioritize verified, then pending, then most recent)
      const verifiedDoc = matchingDocs.find((d) => d.verification_status === 'verified');
      const pendingDoc = matchingDocs.find((d) => d.verification_status === 'pending');
      const rejectedDoc = matchingDocs.find((d) => d.verification_status === 'rejected');
      const bestDoc = verifiedDoc || pendingDoc || matchingDocs[0];

      // Check expiry status
      if (bestDoc.expiry_date) {
        const daysUntilExpiry = differenceInDays(new Date(bestDoc.expiry_date), new Date());
        
        if (daysUntilExpiry < 0) {
          return {
            documentType: docType,
            status: 'expired',
            uploadedDocument: bestDoc,
            expiryWarning: 'Document has expired',
            daysUntilExpiry,
          };
        }
        
        if (daysUntilExpiry <= 30 && bestDoc.verification_status === 'verified') {
          return {
            documentType: docType,
            status: 'expiring_soon',
            uploadedDocument: bestDoc,
            expiryWarning: `Expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}`,
            daysUntilExpiry,
          };
        }
      }

      // Check verification status
      if (bestDoc.verification_status === 'verified') {
        return {
          documentType: docType,
          status: 'verified',
          uploadedDocument: bestDoc,
          daysUntilExpiry: bestDoc.expiry_date 
            ? differenceInDays(new Date(bestDoc.expiry_date), new Date())
            : undefined,
        };
      }

      if (bestDoc.verification_status === 'rejected') {
        return {
          documentType: docType,
          status: 'rejected',
          uploadedDocument: bestDoc,
          rejectionReason: (bestDoc as any).rejection_reason || 'Document did not meet requirements',
        };
      }

      // Default to pending
      return {
        documentType: docType,
        status: 'pending',
        uploadedDocument: bestDoc,
      };
    });
  }, [settings, documents]);

  const stats = useMemo(() => {
    const total = requiredDocuments.length;
    const verified = requiredDocuments.filter((d) => d.status === 'verified').length;
    const missing = requiredDocuments.filter((d) => d.status === 'missing').length;
    const expiring = requiredDocuments.filter((d) => d.status === 'expiring_soon').length;
    const rejected = requiredDocuments.filter((d) => d.status === 'rejected').length;
    const expired = requiredDocuments.filter((d) => d.status === 'expired').length;
    const pending = requiredDocuments.filter((d) => d.status === 'pending').length;

    const completionPercentage = total > 0 ? Math.round((verified / total) * 100) : 0;
    const isPrequalified = total > 0 && verified === total && expiring === 0 && expired === 0;

    return {
      total,
      verified,
      missing,
      expiring,
      rejected,
      expired,
      pending,
      completionPercentage,
      isPrequalified,
    };
  }, [requiredDocuments]);

  // Sort: missing first, then rejected, then expiring, then pending, then verified
  const sortedDocuments = useMemo(() => {
    const priority = {
      missing: 0,
      rejected: 1,
      expired: 2,
      expiring_soon: 3,
      pending: 4,
      verified: 5,
    };

    return [...requiredDocuments].sort((a, b) => {
      return priority[a.status] - priority[b.status];
    });
  }, [requiredDocuments]);

  return {
    requiredDocuments: sortedDocuments,
    stats,
    isLoading: settingsLoading || documentsLoading,
  };
}
